// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { useWatch } from 'antd/lib/form/Form';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  createProductsOfMonth,
  getProductsList,
  getProductsOfMonthList,
  productsSelector,
  updateProductsOfMonth,
} from '../../feature';

// Components
import { IFormInstance, showSuccessToast } from '@/components';
import { ProductOfMonthForm } from '../../components';

// Models
import { IProduct } from '@/models/products.model';

// Modules
import { usePartnersListSearch } from '@/modules/Partners';

// Helpers
import { CreateProductDTO } from '../../dtos';
import { ProductsOfMonthFields, TGetProductsOfMonthForm } from '../../helpers';

type TCreateProductOfMonthContainer = {
  currentProduct: IProduct | null;
  formInstance: IFormInstance;
  handleCloseDrawer: () => void;
  isLoading: boolean;
};

const CreateProductOfMonthContainer = ({
  formInstance,
  currentProduct,
  handleCloseDrawer,
  isLoading,
}: TCreateProductOfMonthContainer) => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { productsList, productsOfMonthQuery } = useAppState(productsSelector);
  const { partnersList, searchPartners } = usePartnersListSearch();
  const partnerId = useWatch(ProductsOfMonthFields.PARTNER_ID, formInstance);
  const isEdit = !!currentProduct?.id;

  const handleSubmit = useCallback(
    async (values: TGetProductsOfMonthForm) => {
      const dto = new CreateProductDTO(values);

      if (isEdit) {
        await dispatch(updateProductsOfMonth({ id: currentProduct?.id, params: dto })).unwrap();
      } else {
        await dispatch(createProductsOfMonth(dto)).unwrap();
      }

      await dispatch(getProductsOfMonthList({ page: 1 })).unwrap();

      handleCloseDrawer();
      showSuccessToast({ message: t(isEdit ? 'data_saved' : 'data_saved_weekly') });
    },
    [handleCloseDrawer, isEdit, currentProduct, productsOfMonthQuery],
  );

  const debouncedFetchProducts = useCallback(
    debounce((searchText: string) => {
      dispatch(getProductsList({ sku: searchText, ...(partnerId && { partner_ids: [partnerId] }) }));
    }, 1000),
    [partnerId],
  );

  useEffect(() => {
    dispatch(getProductsList({ name: '', ...(partnerId && { partner_ids: [partnerId] }) }));
  }, [partnerId]);

  return (
    <ProductOfMonthForm
      formInstance={formInstance}
      initialValues={currentProduct}
      productsList={productsList?.data}
      handleSubmit={handleSubmit}
      isEdit={isEdit}
      isLoading={isLoading}
      partnerList={partnersList}
      searchPartners={searchPartners}
      debouncedFetchProducts={debouncedFetchProducts}
    />
  );
};

export default CreateProductOfMonthContainer;
